import request from '@/utils/request'

// Features promised to an address before it had an account.
// Backend: /api/v2/admin/pending-grants (request baseURL already includes /api/v2).

export interface PendingGrant {
  id: string
  email: string
  featureKey: string
  durationType: string
  durationValue: number | null
  source: string
  sourceReference: string | null
  // pending | claimed | cancelled
  status: string
  claimedByUserId: string | null
  claimedAt: string | null
  // register | signin | email_change | on_create | sweep
  claimedVia: string | null
  cancelledReason: string | null
  notes: string | null
  createdAt: string
  // How long it has been waiting. The number that says whether somebody needs
  // to go and find this buyer.
  ageDays: number
}

export interface PendingGrantListResult {
  total: number
  grants: PendingGrant[]
}

export const listPendingGrants = (params: { status?: string, search?: string, limit?: number }) =>
  request({ url: '/admin/pending-grants', method: 'get', params })

export const createPendingGrant = (data: {
  email: string
  featureKey: string
  durationType: string
  durationValue?: number | null
  source?: string
  sourceReference?: string
  notes?: string
}) => request({ url: '/admin/pending-grants', method: 'post', data })

// The escape hatch for the address mismatch: give this promise to a named
// account regardless of the address it was recorded against.
export const assignPendingGrant = (id: string, targetUserId: string) =>
  request({ url: `/admin/pending-grants/${id}/assign`, method: 'post', data: { targetUserId } })

export const cancelPendingGrant = (id: string, reason: string) =>
  request({ url: `/admin/pending-grants/${id}/cancel`, method: 'post', data: { reason } })

// Re-checks every pending row against the users table - the safety net for the
// hooks that fire on registration, sign-in and an email correction.
export const sweepPendingGrants = () =>
  request({ url: '/admin/pending-grants/sweep', method: 'post' })

export interface HubSpotImportResult {
  apply: boolean
  since: string
  summary: {
    dealsSeen: number
    grantsCreated: number
    claimedImmediately: number
    withoutContactEmail: number
    withoutFeatures: number
  }
  deals: Array<{
    dealId: string
    dealName: string
    closeDate: string
    skus: string[]
    features: string[]
    email: string | null
    outcome: string
  }>
}

// Nothing is written unless `apply` is true.
export const importHubSpotDeals = (data: {
  sinceHours?: number
  limit?: number
  dealIds?: string[]
  apply?: boolean
}) => request({ url: '/admin/hubspot/import', method: 'post', data })

export interface HubSpotContactImportResult {
  apply: boolean
  since: string
  summary: {
    contactsSeen: number
    grantsCreated: number
    claimedImmediately: number
    unreadable: number
    withoutEmail: number
  }
  contacts: Array<{
    contactId: string
    email: string | null
    // What sales typed into the contact field, verbatim.
    raw: string | null
    features: string[]
    unknownTokens: string[]
    outcome: string
  }>
}

// The path sales actually uses: a field on the contact rather than line items
// on a deal. Also runs hourly on its own; this is for not waiting.
export const importHubSpotContacts = (data: {
  sinceHours?: number
  limit?: number
  emails?: string[]
  apply?: boolean
}) => request({ url: '/admin/hubspot/import-contacts', method: 'post', data })
